import { useState } from "react";
import { AlertTriangle, Check, ExternalLink, Package, Sparkles, X } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import { Badge, Button, Card, EmptyState, Field, NumberStepper, PageLoader, SectionTitle, Segmented, Textarea } from "@/components/ui";
import { Avatar, Points } from "@/components/bits";
import { api } from "@/lib/api";
import { REPEAT_LABELS, timeAgo } from "@/lib/format";
import { useAction, useApprovals } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { approvalsForKid, setSelectedKid, useSelectedKid } from "@/lib/selectedKid";
import { describeDays } from "@shared/time";
import type { CompletionCheckDTO, JobDTO, JobRunDTO, PitchReviewDTO, RedemptionDTO, TaskSuggestionDTO } from "@shared/types";

function KidLine({ kidId, children }: { kidId: string; children?: React.ReactNode }) {
  const { memberById } = useSession();
  const kid = memberById(kidId);
  return (
    <div className="flex items-center gap-2 text-sm font-bold text-muted">
      {kid && <Avatar member={kid} size="sm" />}
      <span className="text-ink">{kid?.name ?? "Kid"}</span>
      {children}
    </div>
  );
}

export function JobRunItem({ run }: { run: JobRunDTO }) {
  const { family } = useSession();
  const [reward, setReward] = useState(run.job.reward);
  const [note, setNote] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const approve = useAction(() => api.post(`/job-runs/${run.id}/review`, { action: "approve", reward, note: note || undefined }), {
    success: `Approved — ${reward} ${family.currencyName} paid`,
  });
  const reject = useAction(() => api.post(`/job-runs/${run.id}/review`, { action: "reject", note: note || undefined }), {
    success: "Sent back with your note",
    onSuccess: () => setRejecting(false),
  });
  return (
    <Card>
      <KidLine kidId={run.kidId}>
        <span className="font-semibold">· {timeAgo(run.submittedAt ?? run.claimedAt)}</span>
      </KidLine>
      <div className="mt-2 flex items-center gap-3">
        <span className="text-3xl">{run.job.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="text-lg font-extrabold">{run.job.title}</div>
          {run.job.workItOff && <Badge tone="violet">Work-it-off: pays back toward zero</Badge>}
        </div>
      </div>
      {run.kidNote && <p className="mt-2 rounded-2xl bg-sunken px-3 py-2 text-sm">💬 “{run.kidNote}”</p>}
      <div className="mt-3 flex items-center gap-2">
        <div className="flex-1">
          <NumberStepper value={reward} onChange={(v) => setReward(Math.round(v))} min={0} step={5} />
        </div>
      </div>
      {reward !== run.job.reward && (
        <p className="mt-1 text-xs font-bold text-muted">
          Listed at {run.job.reward} — {reward > run.job.reward ? "adding a bonus for great work" : "partial credit"}
        </p>
      )}
      <div className="mt-3 grid grid-cols-[auto_1fr] gap-2">
        <Button variant="secondary" onClick={() => setRejecting(true)}>
          <X className="size-4" /> Not yet
        </Button>
        <Button onClick={() => approve.mutate()} loading={approve.isPending}>
          <Check className="size-4" /> Approve · <Points value={reward} family={family} />
        </Button>
      </div>
      <Sheet
        open={rejecting}
        onClose={() => setRejecting(false)}
        title="What's left to do?"
        footer={
          <Button block variant="danger" onClick={() => reject.mutate()} loading={reject.isPending}>
            Send back
          </Button>
        }
      >
        <p className="mb-3 text-sm text-muted">No reward is paid. They can finish up and submit again.</p>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="The corners still need vacuuming" />
      </Sheet>
    </Card>
  );
}

export function PitchItem({ job }: { job: JobDTO }) {
  const { family } = useSession();
  const [reward, setReward] = useState(job.pitchReward ?? job.reward);
  const [repeat, setRepeat] = useState(job.repeat);
  const [note, setNote] = useState("");
  const [advice, setAdvice] = useState<PitchReviewDTO | null>(null);
  const check = useAction(() => api.post<PitchReviewDTO>(`/advisor/review-pitch/${job.id}`), { onSuccess: (r) => setAdvice(r) });
  const review = useAction(
    (action: "approve" | "reject") => api.post(`/jobs/${job.id}/review-pitch`, { action, reward, repeat, note: note || undefined }),
    { success: (r) => ((r as JobDTO).status === "active" ? "Pitch approved — it's live!" : "Pitch declined") },
  );
  return (
    <Card>
      <KidLine kidId={job.pitchedBy ?? ""}>
        <span className="font-semibold">pitched · {timeAgo(job.createdAt)}</span>
      </KidLine>
      <div className="mt-2 flex items-center gap-3">
        <span className="text-3xl">{job.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="text-lg font-extrabold">{job.title}</div>
          <div className="text-sm text-muted">
            Asking <Points value={job.pitchReward ?? job.reward} family={family} />
            {job.estimatedMinutes ? ` · ~${job.estimatedMinutes} min` : ""} · {REPEAT_LABELS[job.repeat]}
          </div>
        </div>
      </div>
      {job.description && <p className="mt-2 text-sm">{job.description}</p>}
      {job.pitchNote && <p className="mt-2 rounded-2xl bg-violet-soft px-3 py-2 text-sm">💡 “{job.pitchNote}”</p>}

      {advice ? (
        <div className="mt-3 rounded-2xl bg-sunken p-3 text-sm">
          <div className="flex items-center gap-2 font-extrabold">
            <Sparkles className="size-4 text-violet" />
            {advice.verdict === "fair" ? "Fair price" : advice.verdict === "high" ? "A bit high" : "Undervalued"} · suggest{" "}
            <Points value={advice.suggestedReward} family={family} /> ({advice.low}–{advice.high})
          </div>
          <p className="mt-1 text-muted">{advice.rationale}</p>
          <p className="mt-1 font-semibold">Tip: {advice.coachingTip}</p>
          <Button size="sm" variant="soft" className="mt-2" onClick={() => setReward(advice.suggestedReward)}>
            Counter with {advice.suggestedReward}
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="ghost" className="mt-2 text-violet" onClick={() => check.mutate()} loading={check.isPending}>
          <Sparkles className="size-4" /> Is this price fair?
        </Button>
      )}

      <div className="mt-3 space-y-3">
        <Field label={reward === (job.pitchReward ?? job.reward) ? "Reward (their price)" : "Your counter-offer"}>
          <NumberStepper value={reward} onChange={(v) => setReward(Math.round(v))} min={1} step={5} />
        </Field>
        <Segmented
          value={repeat}
          onChange={setRepeat}
          options={[
            { value: "once", label: "Once" },
            { value: "daily", label: "Daily" },
            { value: "weekly", label: "Weekly" },
            { value: "anytime", label: "Any time" },
          ]}
        />
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Message to your entrepreneur (optional)" className="min-h-[60px]" />
      </div>
      <div className="mt-3 grid grid-cols-[auto_1fr] gap-2">
        <Button variant="secondary" onClick={() => review.mutate("reject")} loading={review.isPending && review.variables === "reject"}>
          Decline
        </Button>
        <Button onClick={() => review.mutate("approve")} loading={review.isPending && review.variables === "approve"}>
          {reward === (job.pitchReward ?? job.reward) ? "Approve" : "Approve with counter-offer"}
        </Button>
      </div>
    </Card>
  );
}

/** A responsibility or job a connected assistant recommended. Approving creates it for the kids. */
function SuggestionItem({ s }: { s: TaskSuggestionDTO }) {
  const { family, memberById } = useSession();
  const [reward, setReward] = useState(s.reward ?? 0);
  const review = useAction(
    (action: "approve" | "dismiss") =>
      api.post(`/suggestions/${s.id}/review`, { action, ...(action === "approve" && s.kind === "job" ? { reward } : {}) }),
    { success: (r) => ((r as { created?: unknown }).created ? `Added: ${s.title}` : "Suggestion dismissed") },
  );
  const forWhom = s.kidIds.length ? s.kidIds.map((id) => memberById(id)?.name ?? "Kid").join(", ") : "Every kid";
  return (
    <Card>
      <div className="flex items-center gap-2 text-sm font-bold text-muted">
        <Badge tone={s.kind === "job" ? "coin" : "brand"}>{s.kind === "job" ? "Paid job" : "Responsibility"}</Badge>
        <span>
          {forWhom} · {s.source ? `from ${s.source}` : "suggested"} · {timeAgo(s.createdAt)}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <span className="text-3xl">{s.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="text-lg font-extrabold">{s.title}</div>
          <div className="text-sm text-muted">
            {s.kind === "job" ? (
              <>
                {REPEAT_LABELS[s.repeat ?? "anytime"]}
                {s.estimatedMinutes ? ` · ~${s.estimatedMinutes} min` : ""}
                {s.workItOff ? " · work-it-off" : ""}
              </>
            ) : (
              <>
                {describeDays(s.daysOfWeek ?? 127)}
                {s.frequency === "weekly" ? " · weekly" : ""}
                {s.dueTime ? ` · by ${s.dueTime}` : ""}
              </>
            )}
          </div>
        </div>
      </div>
      {s.description && <p className="mt-2 text-sm">{s.description}</p>}
      {s.reason && (
        <p className="mt-2 flex gap-1.5 rounded-2xl bg-violet-soft px-3 py-2 text-sm">
          <Sparkles className="mt-0.5 size-4 shrink-0 text-violet" /> {s.reason}
        </p>
      )}
      {s.kind === "job" && (
        <div className="mt-3">
          <Field label={`Reward (${family.currencyName})`}>
            <NumberStepper value={reward} onChange={(v) => setReward(Math.round(v))} min={0} step={5} />
          </Field>
        </div>
      )}
      <div className="mt-3 grid grid-cols-[auto_1fr] gap-2">
        <Button variant="secondary" onClick={() => review.mutate("dismiss")} loading={review.isPending && review.variables === "dismiss"}>
          Dismiss
        </Button>
        <Button onClick={() => review.mutate("approve")} loading={review.isPending && review.variables === "approve"}>
          <Check className="size-4" /> Approve
        </Button>
      </div>
    </Card>
  );
}

export function RedemptionItem({ r }: { r: RedemptionDTO }) {
  const { family } = useSession();
  const review = useAction((action: "approve" | "deny") => api.post(`/redemptions/${r.id}/review`, { action }), {
    success: (x) => ((x as RedemptionDTO).status === "denied" ? "Request declined" : "Approved"),
  });
  return (
    <Card>
      <KidLine kidId={r.kidId}>
        <span className="font-semibold">· {timeAgo(r.createdAt)}</span>
      </KidLine>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-lg font-extrabold">{r.title}</div>
          <Points value={r.points} family={family} className="text-coin-ink" />
        </div>
        {r.externalUrl && (
          <a href={r.externalUrl} target="_blank" rel="noreferrer noopener" className="flex items-center gap-1 text-sm font-bold text-brand">
            View <ExternalLink className="size-4" />
          </a>
        )}
      </div>
      {r.kidNote && <p className="mt-2 rounded-2xl bg-sunken px-3 py-2 text-sm">💬 “{r.kidNote}”</p>}
      {r.warnings.map((w) => (
        <p key={w} className="mt-2 flex items-center gap-1.5 text-sm font-bold text-warn">
          <AlertTriangle className="size-4" /> {w}
        </p>
      ))}
      <div className="mt-3 grid grid-cols-[auto_1fr] gap-2">
        <Button variant="secondary" onClick={() => review.mutate("deny")} loading={review.isPending && review.variables === "deny"}>
          Not now
        </Button>
        <Button onClick={() => review.mutate("approve")} loading={review.isPending && review.variables === "approve"}>
          Approve
        </Button>
      </div>
    </Card>
  );
}

export function DeliverItem({ r }: { r: RedemptionDTO }) {
  const fulfill = useAction(() => api.post(`/redemptions/${r.id}/fulfill`), { success: "Marked as delivered" });
  const isGoal = r.kind === "goal";
  return (
    <Card className="flex flex-wrap items-center gap-3 p-3">
      <Package className="size-6 shrink-0 text-coin" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-extrabold">{r.title}</div>
        <KidLine kidId={r.kidId} />
      </div>
      <div className="flex gap-2">
        {r.externalUrl ? (
          <a href={r.externalUrl} target="_blank" rel="noreferrer noopener">
            <Button size="sm" variant="secondary">
              Buy <ExternalLink className="size-4" />
            </Button>
          </a>
        ) : (
          isGoal && (
            <a
              href={`https://www.amazon.com/s?k=${encodeURIComponent(r.title.replace(/^\P{L}+/u, "").trim())}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              <Button size="sm" variant="secondary">
                Find on Amazon <ExternalLink className="size-4" />
              </Button>
            </a>
          )
        )}
        <Button size="sm" onClick={() => fulfill.mutate()} loading={fulfill.isPending}>
          {isGoal ? "Purchased" : r.kind === "convert" ? "Paid" : "Delivered"}
        </Button>
      </div>
    </Card>
  );
}

export function CompletionItem({ c }: { c: CompletionCheckDTO }) {
  const review = useAction((action: "approve" | "reject") => api.post(`/completions/${c.id}/review`, { action }));
  return (
    <Card className="flex items-center gap-3 p-3">
      <span className="text-2xl">{c.responsibility.icon}</span>
      <div className="min-w-0 flex-1">
        <div className="truncate font-extrabold">{c.responsibility.title}</div>
        <KidLine kidId={c.kidId}>
          <span className="font-semibold">· {timeAgo(c.createdAt)}</span>
        </KidLine>
      </div>
      <Button size="sm" variant="secondary" className="size-11 px-0" aria-label="Not done" onClick={() => review.mutate("reject")}>
        <X className="size-4" />
      </Button>
      <Button size="sm" className="size-11 px-0" aria-label="Verified" onClick={() => review.mutate("approve")}>
        <Check className="size-4" />
      </Button>
    </Card>
  );
}

export function Approvals() {
  const { data: all, isLoading } = useApprovals();
  const kid = useSelectedKid();
  if (isLoading || !all) return <PageLoader />;
  // With a kid picked in the switcher, only their requests show; the rest of the family is one tap away.
  const data = kid ? approvalsForKid(all, kid.id) : all;
  const others = all.count - data.count;
  const nothing = data.count === 0 && data.toDeliver.length === 0;
  return (
    <div>
      <PageHeader title="Approvals" subtitle={kid ? `For ${kid.name} · nothing is paid until you confirm it` : "Nothing is paid until you confirm it"} bell="/parent/notifications" />
      {kid && others > 0 && (
        <button
          type="button"
          onClick={() => setSelectedKid(null)}
          className="mt-2 flex min-h-11 w-full items-center justify-center rounded-2xl bg-sunken px-3 text-sm font-bold text-brand"
        >
          {others} more from other kids · show all kids
        </button>
      )}
      {nothing && (
        <div className="mt-6">
          <EmptyState icon="✨" title="All caught up">
            {kid ? `Nothing from ${kid.name} right now.` : "Finished jobs, pitches, spend requests and suggested tasks will land here."}
          </EmptyState>
        </div>
      )}
      {data.jobRuns.length > 0 && (
        <>
          <SectionTitle>Finished jobs ({data.jobRuns.length})</SectionTitle>
          <div className="space-y-3">
            {data.jobRuns.map((r) => (
              <JobRunItem key={r.id} run={r} />
            ))}
          </div>
        </>
      )}
      {data.pitches.length > 0 && (
        <>
          <SectionTitle>Job pitches ({data.pitches.length})</SectionTitle>
          <div className="space-y-3">
            {data.pitches.map((j) => (
              <PitchItem key={j.id} job={j} />
            ))}
          </div>
        </>
      )}
      {data.suggestions.length > 0 && (
        <>
          <SectionTitle>Suggested tasks ({data.suggestions.length})</SectionTitle>
          <p className="-mt-1 mb-2 px-1 text-sm text-muted">Recommended by your connected assistant. Kids see them only after you approve.</p>
          <div className="space-y-3">
            {data.suggestions.map((s) => (
              <SuggestionItem key={s.id} s={s} />
            ))}
          </div>
        </>
      )}
      {data.redemptions.length > 0 && (
        <>
          <SectionTitle>Spend requests ({data.redemptions.length})</SectionTitle>
          <div className="space-y-3">
            {data.redemptions.map((r) => (
              <RedemptionItem key={r.id} r={r} />
            ))}
          </div>
        </>
      )}
      {data.completions.length > 0 && (
        <>
          <SectionTitle>Responsibility check-offs ({data.completions.length})</SectionTitle>
          <div className="space-y-2">
            {data.completions.map((c) => (
              <CompletionItem key={c.id} c={c} />
            ))}
          </div>
        </>
      )}
      {data.toDeliver.length > 0 && (
        <>
          <SectionTitle>To hand over ({data.toDeliver.length})</SectionTitle>
          <p className="-mt-1 mb-2 px-1 text-sm text-muted">Approved and paid for — mark them once your kid has them.</p>
          <div className="space-y-2">
            {data.toDeliver.map((r) => (
              <DeliverItem key={r.id} r={r} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
